import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import axios from "axios";

// Components
import CustomButton from "../../components/CustomButton";
import RoundedTable from "../../components/table-component/RoundedTable";
import InformationBox from "../../components/InformationBox";
import Loader from "../../components/Loader";

// Utils
import { formatDate } from "../../utils/toDate";
import useLanguage from "../../utils/useLanguage";

// Assets
import printIcon from "../../assets/printer_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";

// Styles
import classes from "../user-pages/ViewInvoice.module.css";
import ReceiptTemplate from "../cashier-system/ReusableCashierReceipt";
import { Paper } from "@material-ui/core";
import PaperInvoicePrintComponent from "./PaperInvoicePrint";
import SipparLightsInvoiceTemplate from "./SipparLightsInvoiceTemplate";

export default function ViewPaperSellInvoice() {
  // ========== STATE MANAGEMENT ==========
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [loading, setLoading] = useState(false);
  const componentRef = useRef();
  const receiptRef = useRef();

  // Invoice Data States
  const [invoiceData, setInvoiceData] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceNumber: "",
    invoiceDate: "",
    invoiceNote: "",
    invoiceAmount: {
      receivedAmount: 0,
      totalPrice: 0,
      discount: 0,
      finalAmount: 0,
      leftAmount: 0,
    },
  });
  const [customerData, setCustomerData] = useState({
    customerName: "",
    phoneNumber: "",
    address: "",
  });
  const [creatorDetails, setCreatorDetails] = useState({
    id: null,
    name: "",
  });
  const [currency, setCurrencyData] = useState({
    title: "",
    value: "",
  });

  // REFS
  const printSipparRef = useRef(null);
  const defaultInvoiceRef = useRef(null);

  // For receipt template items
  const [receiptItems, setReceiptItems] = useState([]);
  const [receiptTotal, setReceiptTotal] = useState(0);

  const [receiptSubtotal, setReceiptSubtotal] = useState(0);
  const [receiptDiscount, setReceiptDiscount] = useState(0);
  const [receiptReceivedAmount, setReceiptReceivedAmount] = useState(0);
  const [receiptLeftAmount, setReceiptLeftAmount] = useState(0);

  // handle print cases
  const handlePrint = () => {
    const invoiceType = localStorage.getItem("invoiceType");
    switch (invoiceType) {
      case "sipparLights":
        handlePrintSipparInvoice();
        break;
      default:
        handlePrintPaperInvoice();
        break;
    }
  };

  // Table Configuration
  const titles = [
    { value: "categoryName" },
    { value: "amountOfRolls" },
    { value: "dimension" },
    { value: "paperThickness" },
    { value: "quantity" },
    { value: "price" },
    { value: "totalAmount" },
  ];

  const handlePrintSipparInvoice = useReactToPrint({
    contentRef: printSipparRef,
    pageStyle: `
    @page {
      margin: 0 !important;
      size: A4;
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        direction: ${currentDirection};
        width: 100% !important;
        height: 100% !important;
      }
      body {
        -webkit-print-color-adjust: exact;
        direction: ${currentDirection};
        margin: 0 !important;
        padding: 0 !important;
        display: block !important;
      }
      .no-print {
        display: none !important;
      }
    }
  `,
  });

  const handlePrintPaperInvoice = useReactToPrint({
    contentRef: defaultInvoiceRef,
    pageStyle: `
    @page {
      margin: 0 !important;
      size: A4;
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        direction: ${currentDirection};
        width: 100% !important;
        height: 100% !important;
      }
      body {
        -webkit-print-color-adjust: exact;
        direction: ${currentDirection};
        margin: 0 !important;
        padding: 0 !important;
      }
      .no-print {
        display: none !important;
      }
    }
  `,
  });

  // ========== DATA FETCHING ==========
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const userId = parseInt(localStorage.getItem("userId"), 10);

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getOneFabricSellInvoice/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
          },
        }
      );

      if (response?.status === 200) {
        const data = response.data.data;
        setCurrencyData(() => {
          return {
            title: data.currency?.toLowerCase(),
            value: data.currency?.toUpperCase(),
          };
        });

        const formatedInvoice = data.colors.flatMap((color) => {
          const transactions = color.transactions || [];

          if (transactions.length === 0) {
            return [
              [
                { type: "text", value: color.colorCode || "-" },
                { type: "text", value: "-" },
                { type: "text", value: `${color.dimension || "-"} ${t("cm")}` },
                {
                  type: "text",
                  value: `${color.paperThickness || "-"} ${t("gram")}`,
                },
                { type: "text", value: "-" },
                { type: "text", value: "-" },
              ],
            ];
          }

          return transactions.map((tx) => {
            const quantityWithUnit = tx.quantity
              ? `${tx.quantity} ${t("ton") || ""}`
              : "-";
            const amountOfRolls = `${tx.amountOfRolls} ${t("rollUnit")}`;
            const priceWithCurrency = tx.price
              ? `${tx.price} ${tx.currency || ""}`
              : "-";
            const paperThickness = `${color.paperThickness || "-"} ${
              t("gram") || ""
            }`;
            const dimension = `${color.dimension || "-"} ${t("cm") || ""}`;
            const totalAmount = `${tx.price * tx.quantity} ${tx.currency}`;

            return [
              { type: "text", value: color.colorCode || "-" },
              { type: "text", value: amountOfRolls },
              { type: "text", value: dimension },
              { type: "text", value: paperThickness },
              { type: "text", value: quantityWithUnit },
              { type: "text", value: priceWithCurrency },
              { type: "text", value: totalAmount },
            ];
          });
        });

        setInvoiceData(formatedInvoice);
        setInvoiceDetails({
          invoiceNumber: data.id,
          invoiceDate: formatDate(data.date),
          invoiceNote: data.notes,
          invoiceAmount: {
            receivedAmount: data.receivedAmount,
            totalPrice: data.totalPrice,
            discount: data.discount,
            finalAmount: data.finalAmount,
            leftAmount: data.leftAmount,
          },
        });
        setCustomerData({
          customerName: data.customer.name,
          phoneNumber: data.customer.phoneNumber,
          address: data.customer.address,
        });
        setCreatorDetails(() => {
          return {
            id: data.user.id,
            name: data.user.name,
          };
        });

        const formattedReceiptItems = data.colors.map((color) => {
          const transaction = color.transaction || {};
          const quantity = transaction.quantity || 0;
          const price = transaction.price || 0;
          const total = quantity * price;

          return {
            name: color.colorName || "-",
            quantity: quantity,
            price: price,
            total: total,
          };
        });

        setReceiptItems(formattedReceiptItems);

        setReceiptTotal(data.finalAmount || 0);
        setReceiptSubtotal(data.totalPrice || 0);
        setReceiptDiscount(data.discount || 0);
        setReceiptReceivedAmount(data.receivedAmount || 0);
        setReceiptLeftAmount(data.leftAmount || 0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ========== UI HANDLERS ==========
  const handlePrintOriginal = useReactToPrint({
    contentRef: componentRef,
    pageStyle: `
                @page { size: auto; margin: 5mm; }
                @media print {
                  body { -webkit-print-color-adjust: exact;
                direction: ${currentDirection};
                  }
                  .no-print { display: none !important; }
                }
              `,
  });

  // Receipt print handler
  const handlePrintReceipt = useReactToPrint({
    contentRef: receiptRef,
    pageStyle: `
      @page { size: 80mm auto; margin: 0; }
      @media print {
        body { 
          -webkit-print-color-adjust: exact;
          direction: ${currentDirection};
        }
      }
    `,
  });

  // ========== RENDER ==========
  if (loading) return <Loader fullHeight />;

  return (
    <>
      <main className={classes.viewInvoicePage}>
        <div className="e-container" ref={componentRef}>
          {/* Header Section */}
          <div className="user-logout">
            <CustomButton
              title={t("back")}
              variant="blank"
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={14}
              onClick={() => navigate(-1)}
            />

            <div className="o-row u-margin--top_500">
              <p className={classes.userTitle}>{`${t("user")}: `}</p>
              <p className={classes.userName}>
                {localStorage.getItem("username")}
              </p>
            </div>
          </div>

          {/* Invoice Details Section */}
          <section className="o-row u-margin--top_300">
            <h3 className={classes.invoiceDetailsTitle}>
              {t("invoiceDetails")}
            </h3>
          </section>

          <section className="o-column-row-switcher o-row--horizontal-align_space-between u-margin--top_100 u-margin--bottom_300">
            {/* Column 1 */}
            <div className="o-column o-column--gap-size_300 u-padding--top_100">
              <div className="o-row o-row--gap-size_300">
                <span className="t-bold_bold u-min-width-specific_100">
                  {t("customerName")}:
                </span>{" "}
                <span>
                  {customerData?.customerName
                    ? customerData?.customerName
                    : t("undefined")}
                </span>
              </div>
              <div className="o-row o-row--gap-size_300">
                <span className="t-bold_bold u-min-width-specific_100">
                  {t("invoiceNumber")}:
                </span>{" "}
                <span>{invoiceDetails?.invoiceNumber}</span>
              </div>

              <div className="o-row o-row--gap-size_300">
                <span className="t-bold_bold u-min-width-specific_100">
                  {t("Date")}:
                </span>
                <span>{invoiceDetails.invoiceDate}</span>
              </div>
            </div>

            {/* Column 2 */}
            <div className="o-column o-column--gap-size_300 u-padding--top_600">
              <div className="o-row o-row--gap-size_300">
                <span className="t-bold_bold u-min-width-specific_100">
                  {t("creator")}:
                </span>
                <span>{creatorDetails?.name || t("undefined")}</span>
              </div>
            </div>
          </section>

          {/* Items Table */}
          {invoiceData.length === 0 ? (
            <InformationBox>{t("noDetails")}</InformationBox>
          ) : (
            <RoundedTable titles={titles} rows={invoiceData} />
          )}

          {/* Notes Section */}
          <section className={classes.section}>
            <div className={classes.col}>
              <span className={`${classes.colTitle} notes-class`}>
                {t("notes")}: {invoiceDetails.invoiceNote}
              </span>
            </div>
          </section>

          {/* Action Buttons */}
          <section className={classes.actions}>
            <>
              <CustomButton
                title={t("print")}
                onClick={handlePrint}
                variant="gray"
                icon={printIcon}
                iconSize={18}
              />
            </>
          </section>
        </div>

        {/* Default printing template */}
        <PaperInvoicePrintComponent
          ref={defaultInvoiceRef}
          invoiceData={invoiceData}
          invoiceDetails={invoiceDetails}
          customerData={customerData}
          creatorDetails={creatorDetails}
          currency={currency}
        />

        {/* Sippar lights template */}
        <SipparLightsInvoiceTemplate
          ref={printSipparRef}
          invoiceData={{
            // Company info
            companyName: "Sippar Lights",
            companyTagline: "Trading and Services",

            // Invoice details
            invoiceNumber: invoiceDetails?.invoiceNumber,
            date: invoiceDetails?.invoiceDate,

            // Customer and seller info
            sellerName: "SIPPAR LIGHTS",
            sellerLocation: "IRAQ - BAGHDAD",
            buyerName: customerData?.customerName,
            buyerLocation: customerData?.address,

            // Financial data
            totalPrice: invoiceDetails?.invoiceAmount?.totalPrice || 0,
            finalPrice: invoiceDetails?.invoiceAmount?.finalAmount || 0,
            discountPercentage: invoiceDetails?.invoiceAmount?.discount
              ? (
                  (invoiceDetails.invoiceAmount.discount /
                    invoiceDetails.invoiceAmount.totalPrice) *
                  100
                ).toFixed(0)
              : 0,

            // Items - map your invoice data to the expected format
            items:
              invoiceData?.map((item) => ({
                description:
                  `${item[0]?.value} ${item[2]?.value.split(" ")[0]}cm ${
                    item[3]?.value.split(" ")[0]
                  }gsm (${item[1]?.value.split(" ")[0]} roll)` || "-",
                quantity: `${item[4]?.value.split(" ")[0]} TON` || "-",
                pricePerTon:
                  parseFloat(item[5]?.value?.replace(/[^\d.]/g, "")) || 0,
                totalPrice:
                  parseFloat(item[6]?.value?.replace(/[^\d.]/g, "")) || 0,
              })) || [],

            // Calculate total tons from your data
            totalTons:
              invoiceData?.reduce((total, item) => {
                const quantity = item[4]?.value?.replace(/[^\d.]/g, "") || "0";
                return total + parseFloat(quantity);
              }, 0) + " TON" || "0 KG",
          }}
        />
      </main>
    </>
  );
}
